/**
 * CompanyDashboardAllObjectiveDrawerSideBar
 *
 * Side panel for the add-all-objective drawer. Handles creating the initial Objective,
 * adding a description, listing Key Results, selecting one, and launching owner/creation
 * popups. Controls are permission-gated and many actions are disabled until the
 * objective is saved.
 */
import React, { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import moment from "moment";
import ownerImage from "../../../../../assets/icons/userProfileIcone.png";
import CompanyDashboardAddKeyResultsPopup from "../../company-dashboard-add-popup/company-dashboard-add-key-results-popup/company-dashboard-add-key-results-popup";
import CompanyDashboardAddObjectivePopup from "../../company-dashboard-add-popup/company-dashboard-add-object-popup/company-dashboard-add-object-popup";
import addCirculIcon from "../../../../../assets/icons/addCirculIcon.svg";
import addREC from "../../../../../assets/icons/addREC.svg";
import { getProgressColor } from "../../../company-dashboard-utils";
import "./company-dashboard-all-objective-drawer-side-bar.scss";
import CompanyDashboardAddObjectiveDescriptionPopup from "../../company-dashboard-add-popup/company-dashboard-add-object-description-popup/company-dashboard-add-object-description-popup";
import { Tooltip } from "@mui/material";
import { usePermissions } from "../../../../../common/hooks/usePermissions";
import MODULES from "../../../../../constants/modules";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import CompanyDashboardAddKeyResultsOwnerPopup from "../../company-dashboard-all-objective-drawer/company-dashboard-all-objective-drawer-side-bar/company-dashboard-objective-drawer-side-bar-key-results-owner-popup";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

const CompanyDashboardAllObjectiveDrawerSideBar: any = (props: any) => {
  const {
    newObjectiveData,
    setNewObjectiveData,
    descriptionData,
    setDescriptionData,
    selectedKeyResult,
    setSelectedKeyResult,
    employeesList,
    setListOfKeyResultsData,
    listOfKeyResultsData,
    saveObjective,
    saveDescription,
    saveKeyresult,
    statusListObjectives,
    statusListKeyResults,
    setLoading,
  } = props;
  const [isOpenAddKeyResultsPopup, setIsOpenAddKeyResultsPopup] =
    useState(false);
  const [isOpenAddObjectivePopup, setIsOpenAddObjectivePopup] = useState(false);
  const [isOpenDescriptionPopup, setIsOpenDescriptionPopup] = useState(false);
  const [isOpenAddKeyResultsOwnerPopup, setIsOpenAddKeyResultsOwnerPopup] =
    useState(false);
  const [setAlertOpen] = useState(false);
  const [setAlertMessage] = useState("");
  const [setAlertType] = useState<"error" | "success" | "info">("success");

  const { canWrite: canWritKeyResultss } = usePermissions(MODULES.KEY_RESULTS);
  const { canEdit: canEditObjectives } = usePermissions(MODULES.OBJECTIVES);

  // helper booleans
  const isObjectiveSaved = !!newObjectiveData; // true only after name/objective saved
  const descText =
    descriptionData && typeof descriptionData === "object"
      ? descriptionData.description
      : descriptionData;

  const handleOpenAddKeyResultsPopup = () => {
    if (!isObjectiveSaved) return; // guard - do nothing when disabled
    setIsOpenAddKeyResultsPopup(true);
  };

  const handleCloseAddKeyResultsOwnerPopup = () => {
    setIsOpenAddKeyResultsOwnerPopup(false);
  };

  const handleOpenKeyResultOwnerPopup = (keyResult: any) => {
    setSelectedKeyResult(keyResult);
    setIsOpenAddKeyResultsOwnerPopup(true);
  };

  const handleCloseAddKeyResultsPopup = () => {
    setIsOpenAddKeyResultsPopup(false);
  };

  const handleOpenAddObjectivePopup = () => {
    setIsOpenAddObjectivePopup(true);
  };

  const handleCloseAddObjectivePopup = () => {
    setIsOpenAddObjectivePopup(false);
  };
  const handleOpenAddObjectiveDescriptionPopup = () => {
    if (!isObjectiveSaved) return; // guard
    setIsOpenDescriptionPopup(true);
  };
  const handleCloseAddObjectiveDescriptionPopup = () => {
    setIsOpenDescriptionPopup(false);
  };

  const stringAvatar = (name: string) => {
    if (!name) return { children: "?" };
    const parts = name.split(" ");
    const initials =
      parts.length === 1 ? parts[0][0] : `${parts[0][0]}${parts[1][0]}`;
    return {
      sx: { bgcolor: "#403c6f" },
      children: initials.toUpperCase(),
    };
  };

  const handleSelect = (keyResult: any) => {
    const updatedList = listOfKeyResultsData.map((kr: any) => ({
      ...kr,
      isSelected: Number(kr.id) === Number(keyResult.id),
    }));

    setListOfKeyResultsData(updatedList);
    setSelectedKeyResult(keyResult);
  };

  return (
    <>
      <OverlayScrollbarsComponent
        className="company-dashboard-all-objective-add-drawer-side-bar-content company-dashboard-all-objective-drawer-side-bar-content"
        options={{
          scrollbars: {
            theme: "os-theme-dark",
            autoHide: "scroll",
            autoHideDelay: 400,
          },
        }}
      >
        <div className="company-dashboard-all-objective-add-drawer-side-bar-content-header">
          Name
        </div>
        {newObjectiveData ? (
          <div
            className="company-dashboard-all-objective-drawer-side-bar-content-details"
            style={{
              borderBottom: `3px solid ${getProgressColor(newObjectiveData?.status).mainColor}`,
            }}
          >
            <div className="company-dashboard-all-objective-drawer-side-bar-content-details-content">
              <div>
                <div className="company-dashboard-all-objective-drawer-side-bar-content-details-content-main-label">
                  {newObjectiveData?.objectiveName}
                </div>
                <div className="company-dashboard-all-objective-drawer-side-bar-content-details-content-sub-label">
                  Last Updated:
                  {moment(newObjectiveData?.updatedAt).format("MM-DD-YYYY")}
                </div>
              </div>
              <div className="company-dashboard-all-objective-drawer-side-bar-content-details-content-graph">
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={72}
                    thickness={6.2}
                    sx={{
                      color: getProgressColor(newObjectiveData?.status)
                        .lightColor,
                    }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={newObjectiveData?.progress || 0}
                    size={72}
                    sx={{
                      color: getProgressColor(newObjectiveData?.status)
                        .mainColor,
                      position: "absolute",
                      left: 0,
                      "& .MuiCircularProgress-circle": {
                        strokeLinecap: "round",
                      },
                    }}
                    thickness={6.2}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ color: "#403C6F" }}
                    >
                      {`${Math.round(newObjectiveData?.progress || 0)}%`}
                    </Typography>
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        ) : (
          <div className="company-dashboard-all-objective-add-drawer-side-bar-add-content-details">
            <button
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={handleOpenAddObjectivePopup}
            >
              <img
                src={addCirculIcon}
                alt="Owner"
                style={{ cursor: "pointer" }}
              />
            </button>
          </div>
        )}

        <div className="company-dashboard-all-objective-add-drawer-side-bar-content-header">
          Description
        </div>

        {!descText && canEditObjectives ? (
          // Show + icon if no description yet. Disabled until objective saved.
          <div className="company-dashboard-all-objective-add-drawer-side-bar-add-content-details">
            {isObjectiveSaved ? (
              <button
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
                onClick={handleOpenAddObjectiveDescriptionPopup}
              >
                <img
                  src={addCirculIcon}
                  alt="Add Description"
                  style={{ cursor: "pointer" }}
                />
              </button>
            ) : (
              <Tooltip title="Please create an Objective before adding Description">
                <img
                  src={addCirculIcon}
                  alt="Add Description"
                  style={{ opacity: 0.4, cursor: "not-allowed" }}
                />
              </Tooltip>
            )}
          </div>
        ) : (
          // Show saved description inside styled box (same as Name)
          <div
            className="company-dashboard-all-objective-drawer-side-bar-content-details"
            style={{
              maxHeight: "150px", // fixed height
              overflowY: "auto", // vertical scroll
              overflowX: "hidden", // prevent horizontal scroll
              whiteSpace: "normal", // wrap text
              wordWrap: "break-word",
              wordBreak: "break-word",
              padding: "10px",
              border: "1px solid rgba(0, 0, 0, 0.0392156863)",
              borderRadius: "16px",
              boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.0392156863)",
            }}
          >
            <div className="company-dashboard-all-objective-drawer-side-bar-content-details-content">
              <div>
                <div
                  style={{ fontWeight: 400 }}
                  className="company-dashboard-all-objective-drawer-side-bar-content-details-content-main-label"
                >
                  {canEditObjectives
                    ? descText
                    : "No access to edit objective description"}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="company-dashboard-all-objective-add-drawer-side-bar-content-header">
          <div>Key Results</div>
          {canWritKeyResultss && (
            <div className="company-dashboard-all-objective-add-drawer-side-bar-content-header-actions">
              {isObjectiveSaved ? (
                <button
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  onClick={handleOpenAddKeyResultsPopup}
                >
                  <img
                    src={addREC}
                    alt="Add Key Result"
                    style={{ cursor: "pointer" }}
                  />
                </button>
              ) : (
                <Tooltip title="Please create an Objective before adding a Key Result">
                  <img
                    src={addREC}
                    alt="Add Key Result"
                    style={{ opacity: 0.4, cursor: "not-allowed" }}
                  />
                </Tooltip>
              )}
            </div>
          )}
        </div>
        {listOfKeyResultsData?.length > 0 ? (
          listOfKeyResultsData?.map((keyResult: any, index: number) => (
            <button
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                width: "calc(100% - 35px)",
              }}
              key={index + 1}
              onClick={() => {
                handleSelect(keyResult);
              }}
            >
              <div
                className="company-dashboard-all-objective-drawer-side-bar-content-key-results"
                style={
                  keyResult?.isSelected ? { border: "1px solid #6E6D92" } : {}
                }
              >
                <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content">
                  <div>
                    <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-main-label">
                      {keyResult?.title}
                    </div>
                  </div>
                  <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-graph">
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={72}
                        thickness={6.2}
                        sx={{
                          color: getProgressColor(keyResult?.status).lightColor,
                          "& .MuiCircularProgress-circle": {
                            strokeLinecap: "round",
                          },
                        }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={keyResult?.progress || 0}
                        size={72}
                        sx={{
                          color: getProgressColor(keyResult?.status).mainColor,
                          position: "absolute",
                          left: 0,
                          "& .MuiCircularProgress-circle": {
                            strokeLinecap: "round",
                          },
                        }}
                        thickness={6.2}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: "absolute",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ color: "#403C6F" }}
                        >{`${Math.round(keyResult?.progress || 0)}%`}</Typography>
                      </Box>
                    </Box>
                  </div>
                </div>

                <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content">
                  {keyResult?.owners?.length > 0 ? (
                    <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner">
                      <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner-label">
                        Owner
                      </div>
                      <button
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => handleOpenKeyResultOwnerPopup(keyResult)}
                      >
                        <div
                          className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner-img"
                          style={{ cursor: "pointer" }}
                        >
                          <AvatarGroup max={3} spacing={7}>
                            {keyResult.owners.map((owner: any, index: number) => (
                              <Tooltip
                                title={owner.employeeName || ""}
                                key={index + 1}
                              >
                                {owner.profileImage ? (
                                  <Avatar
                                    alt={owner.employeeName}
                                    src={owner.profileImage}
                                  />
                                ) : (
                                  <Avatar {...stringAvatar(owner.employeeName)} />
                                )}
                              </Tooltip>
                            ))}
                          </AvatarGroup>
                        </div>
                      </button>
                    </div>
                  ) : (
                    <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner">
                      <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner-label">
                        Owner
                      </div>
                      <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner-img">
                        <img src={ownerImage} alt="Owner" />
                      </div>
                    </div>
                  )}
                  <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner">
                    <div className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner-label">
                      Weight
                    </div>
                    <div
                      className="company-dashboard-all-objective-drawer-side-bar-content-key-results-content-owner-label"
                      style={{ marginTop: "14px" }}
                    >
                      {keyResult?.weightage || 0}%
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))
        ) : (
          <div className="company-dashboard-all-objective-add-drawer-side-bar-add-content-key-results">
            <div className="company-dashboard-all-objective-add-drawer-side-bar-add-content-key-results-content">
              <div className="company-dashboard-all-objective-add-drawer-side-bar-add-content-key-results-content-label">
                You don't have any key results
              </div>
              {canWritKeyResultss && (
                <div className="company-dashboard-all-objective-add-drawer-side-bar-add-content-key-results-content-button">
                  {isObjectiveSaved ? (
                    <button
                      onClick={handleOpenAddKeyResultsPopup}
                      style={{ cursor: "pointer" }}
                    >
                      Add Key Results
                    </button>
                  ) : (
                    <Tooltip title="Please save Objective name first to add Key Results">
                      <span>
                        <button
                          style={{ opacity: 0.6, cursor: "not-allowed" }}
                          disabled
                        >
                          Add Key Results
                        </button>
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </OverlayScrollbarsComponent>
      <CompanyDashboardAddKeyResultsPopup
        isOpenAddPopup={isOpenAddKeyResultsPopup}
        onCancel={handleCloseAddKeyResultsPopup}
        employeesList={employeesList}
        listOfKeyResultsData={listOfKeyResultsData}
        setListOfKeyResultsData={setListOfKeyResultsData}
        onSave={saveKeyresult}
        statusListKeyResults={statusListKeyResults}
        setLoading={setLoading}
        statusListObjectives={statusListObjectives}
        objectivesData={newObjectiveData}
      />
      <CompanyDashboardAddObjectivePopup
        isOpenAddPopup={isOpenAddObjectivePopup}
        onCancel={handleCloseAddObjectivePopup}
        newObjectiveData={newObjectiveData}
        setNewObjectiveData={setNewObjectiveData}
        employeesList={employeesList}
        onSave={saveObjective}
        statusListObjectives={statusListObjectives}
      />
      <CompanyDashboardAddObjectiveDescriptionPopup
        isOpenAddPopup={isOpenDescriptionPopup}
        onCancel={handleCloseAddObjectiveDescriptionPopup}
        descriptionData={descriptionData}
        setDescriptionData={setDescriptionData}
        employeesList={employeesList}
        onSave={saveDescription}
        newObjectiveData={newObjectiveData}
      />
      {isOpenAddKeyResultsOwnerPopup && (
        <CompanyDashboardAddKeyResultsOwnerPopup
          isOpenAddPopup={isOpenAddKeyResultsOwnerPopup}
          onCancel={handleCloseAddKeyResultsOwnerPopup}
          employeesList={employeesList}
          listKeyResultsData={listOfKeyResultsData}
          setListKeyResultsData={setListOfKeyResultsData}
          setAlertType={setAlertType}
          setAlertOpen={setAlertOpen}
          setLoading={setLoading}
          setAlertMessage={setAlertMessage}
          statusListKeyResults={statusListKeyResults}
          keyResultId={selectedKeyResult?.pk}
          objectiveId={newObjectiveData?.pk}
        />
      )}
    </>
  );
};

export default CompanyDashboardAllObjectiveDrawerSideBar;
